import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Phone, FileText, CreditCard, CheckCircle2, 
  AlertCircle, ShieldCheck, ArrowRight, Zap,
  MessageSquare, User, Smartphone
} from 'lucide-react';
import { Dialog, DialogContent, DialogTrigger } from '../ui/dialog'; // Keep standard imports even if unused to match style

const MorganPatientIntakeSlide = () => {
  const [step, setStep] = useState(0);

  // Animation Cycle
  useEffect(() => {
    const timer = setInterval(() => {
      setStep((prev) => (prev + 1) % 5); // 0, 1, 2, 3, 4 then loop
    }, 3000);
    return () => clearInterval(timer);
  }, []);

  return (
    <div className="h-full w-full bg-slate-50 flex items-center justify-center p-12 relative overflow-hidden">
        {/* Background Elements - Consistent with other slides */}
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden z-0 pointer-events-none">
             <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-blue-100/40 rounded-full blur-[80px] -translate-y-1/2 translate-x-1/2" />
             <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-indigo-100/40 rounded-full blur-[80px] translate-y-1/2 -translate-x-1/2" />
        </div>

        {/* Floating Agent Header (Top Left) */}
        <div className="absolute top-8 left-8 xl:left-12 z-50 flex items-center gap-4 bg-white/40 backdrop-blur-md p-2 pr-6 rounded-full border border-white/20 shadow-lg">
            <div className="w-14 h-14 rounded-full overflow-hidden border-2 border-white shadow-sm">
                 <img src="/assets/morgan.png" alt="Morgan" className="w-full h-full object-cover" />
            </div>
            <div className="text-left">
                <h3 className="text-lg font-bold text-slate-900 leading-none">Morgan</h3>
                <p className="text-xs text-blue-600 font-bold uppercase tracking-wide">Patient Intake</p>
            </div>
        </div>

        <div className="flex flex-col xl:flex-row h-full w-full max-w-[1600px] gap-12 relative z-10 pt-20 xl:pt-0">
            {/* Left Side: Context */}
            <div className="w-full xl:w-[40%] flex flex-col justify-center space-y-8">
                
                <div className="space-y-6">
                    <h2 className="text-4xl lg:text-5xl font-bold text-slate-900 leading-tight">
                        Ensure every patient arrives <span className="text-blue-600">fully prepared</span>.
                    </h2>
                    
                    <p className="text-lg text-slate-600 leading-relaxed">
                        Morgan proactively chases down intake forms and insurance cards so your front desk doesn't have to.
                        <br/><br/>
                        Using intelligent voice and SMS, she guides patients through <strong>Next-Gen Forms</strong> and instantly hands off new insurance data to Amanda for verification.
                    </p>
                </div>

                <div className="grid grid-cols-1 gap-4">
                    <div className="flex items-center gap-4 p-4 bg-white rounded-xl border border-slate-200 shadow-sm">
                        <div className="p-3 bg-blue-50 rounded-lg text-blue-600">
                            <FileText className="w-6 h-6" />
                        </div>
                        <div>
                            <h4 className="font-bold text-slate-900">Forms Done Pre-Visit</h4>
                            <p className="text-sm text-slate-500">Before the patient walks in the door.</p>
                        </div>
                    </div>
                    
                    <div className="flex items-center gap-4 p-4 bg-white rounded-xl border border-slate-200 shadow-sm">
                        <div className="p-3 bg-purple-50 rounded-lg text-purple-600">
                            <CreditCard className="w-6 h-6" />
                        </div>
                        <div>
                            <h4 className="font-bold text-slate-900">Instant Card Capture</h4>
                            <p className="text-sm text-slate-500">Patients text photos of cards; Morgan extracts the data.</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Right Side: Animated Interaction */}
            <div className="w-full xl:w-[60%] h-full flex items-center justify-center">
                <div className="w-full max-w-lg relative">
                    
                    {/* The "Phone" Container */}
                    <div className="bg-white rounded-[2.5rem] border-8 border-slate-900 shadow-2xl overflow-hidden relative h-[600px] w-[340px] mx-auto z-10">
                        {/* Dynamic Island / Notch */}
                        <div className="absolute top-0 left-1/2 -translate-x-1/2 h-6 w-32 bg-slate-900 rounded-b-2xl z-20" />
                        
                        {/* Status Bar */}
                        <div className="h-12 bg-slate-50 flex items-end justify-between px-6 pb-2 text-[10px] font-medium text-slate-400">
                            <span>9:41</span>
                            <div className="flex gap-1">
                                <div className="w-3 h-3 bg-slate-300 rounded-full" />
                                <div className="w-3 h-3 bg-slate-300 rounded-full" />
                            </div>
                        </div>

                        {/* Chat Interface */}
                        <div className="p-4 space-y-4 h-full bg-slate-50/50">
                            
                            {/* Morgan's Message - Always visible */}
                            <motion.div 
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                className="flex gap-3"
                            >
                                <div className="w-8 h-8 rounded-full overflow-hidden flex-shrink-0 border border-slate-200">
                                    <img src="/assets/morgan.png" alt="Morgan" className="w-full h-full object-cover" />
                                </div>
                                <div className="bg-white p-3 rounded-2xl rounded-tl-none shadow-sm border border-slate-100 max-w-[80%]">
                                    <p className="text-xs text-slate-700 leading-relaxed">
                                        Hi Sarah! We're excited to see you tomorrow. It looks like we're missing your insurance card. Could you please upload a photo?
                                    </p>
                                </div>
                            </motion.div>

                            {/* Step 1: Patient Responds (Image) */}
                            <AnimatePresence>
                                {step >= 1 && (
                                    <motion.div 
                                        initial={{ opacity: 0, scale: 0.9, x: 20 }}
                                        animate={{ opacity: 1, scale: 1, x: 0 }}
                                        exit={{ opacity: 0 }}
                                        className="flex flex-row-reverse gap-3"
                                    >
                                        <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center flex-shrink-0">
                                            <User className="w-4 h-4 text-slate-500" />
                                        </div>
                                        <div className="bg-blue-600 p-2 rounded-2xl rounded-tr-none shadow-sm text-white max-w-[80%]">
                                            <div className="bg-blue-500/50 rounded-lg p-2 mb-1 flex items-center gap-2">
                                                <CreditCard className="w-4 h-4 text-white/80" />
                                                <span className="text-[10px] text-white/90">insurance_card.jpg</span>
                                            </div>
                                            <p className="text-xs">Here you go!</p>
                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>

                            {/* Step 2: Morgan Acknowledges */}
                            <AnimatePresence>
                                {step >= 2 && (
                                    <motion.div 
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        className="flex gap-3"
                                    >
                                        <div className="w-8 h-8 rounded-full overflow-hidden flex-shrink-0 border border-slate-200">
                                            <img src="/assets/ana.png" alt="Morgan" className="w-full h-full object-cover" />
                                        </div>
                                        <div className="bg-white p-3 rounded-2xl rounded-tl-none shadow-sm border border-slate-100 max-w-[80%]">
                                            <p className="text-xs text-slate-700">
                                                Thanks! I've sent this to Amanda for instant verification.
                                            </p>
                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>

                             {/* Step 3: Amanda System Notification */}
                             <AnimatePresence>
                                {step >= 3 && (
                                    <motion.div 
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        className="mx-4 mt-8 bg-slate-800 rounded-xl p-3 shadow-lg flex items-center gap-3 border border-slate-700/50 backdrop-blur-sm"
                                    >
                                        <div className="w-8 h-8 rounded-full overflow-hidden flex-shrink-0 border border-slate-600">
                                            <img src="/assets/amanda.png" alt="Amanda" className="w-full h-full object-cover" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="text-[10px] text-slate-400 uppercase font-bold">System Alert</div>
                                            <div className="text-xs text-white font-medium truncate">Benefits Verified: Delta Dental</div>
                                        </div>
                                        <div className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center">
                                            <CheckCircle2 className="w-3 h-3 text-white" />
                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>

                        </div>
                    </div>

                    {/* Background Connecting Lines / Nodes */}
                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[400px] -z-10 hidden xl:block">
                        {/* Node: Form Source */}
                        <motion.div 
                            animate={{ scale: step >= 1 ? 1.1 : 1, opacity: step >= 1 ? 1 : 0.3 }}
                            className="absolute top-10 left-0 bg-white p-3 rounded-xl shadow-lg border border-slate-200 flex items-center gap-3"
                        >
                            <div className="bg-indigo-100 p-2 rounded-lg text-indigo-600">
                                <Zap className="w-5 h-5" />
                            </div>
                            <div className="text-xs font-bold text-slate-700">Next-Gen Forms</div>
                        </motion.div>

                        {/* Node: Verification Dest */}
                        <motion.div 
                            animate={{ scale: step >= 3 ? 1.1 : 1, opacity: step >= 3 ? 1 : 0.3 }}
                            className="absolute bottom-20 right-0 bg-white p-3 rounded-xl shadow-lg border border-slate-200 flex items-center gap-3"
                        >
                             <div className="bg-green-100 p-2 rounded-lg text-green-600">
                                <ShieldCheck className="w-5 h-5" />
                            </div>
                            <div>
                                <div className="text-xs font-bold text-slate-700">Eligibility Check</div>
                                <div className="text-[10px] text-slate-500">Run by Amanda</div>
                            </div>
                        </motion.div>

                        {/* Connecting dashed lines could go here using SVG if needed, but the floating cards add enough context */}
                    </div>

                </div>
            </div>
        </div>
    </div>
  );
};

export default MorganPatientIntakeSlide;
